import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { mockTransactions, mockAssets } from '@/lib/mock-data';
import type { Transaction, TransactionType, TransactionStatus } from '@/types/asset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Download, FileSpreadsheet, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Zap, Filter, Columns, History, FileX, Eye, ArrowRight } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { FiltersTab } from '@/components/RightPanel/FiltersTab';
import { ColumnsTab } from '@/components/RightPanel/ColumnsTab';
import { format } from 'date-fns';
import { exportToCSV, exportToExcel, generateFilename, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { DEFAULT_ENQUIRY_COLUMNS } from '@/lib/enquiry-columns-config';
import { useColumnResize } from '@/hooks/use-column-resize';
import { cn } from '@/lib/utils';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useTransactionHistoryTour } from '@/hooks/use-transaction-history-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';

// Extended transaction type with all required fields
interface ExtendedTransaction extends Transaction {
  auditNo: string;
  assetCode: string;
  reference: string;
  year: number;
  period: number;
  startNBV: number;
  movementValue: number;
  depreciationMovement: number;
  endNBV: number;
  detail: string;
  targetYear?: number;
  targetPeriod?: number;
  subLedger?: string;
  category?: string;
  percentage?: number;
  transferredTo?: string;
  transferredFrom?: string;
  project?: string;
  costCentre?: string;
  disposalReason?: string;
  user?: string;
}

// Helper function to determine asset category based on asset name
function getAssetCategory(assetName: string): string {
  if (assetName.includes('Laptop')) {
    return 'Computer Equipment';
  }
  if (assetName.includes('Desk')) {
    return 'Furniture & Fixtures';
  }
  if (assetName.includes('Vehicle')) {
    return 'Vehicles';
  }
  return 'Machinery';
}

// Helper function to get asset code from asset ID
// For API data, the assetCode should come from the transaction itself
// This is only used as a fallback for demo data
function getAssetCode(assetId: string): string {
  const asset = mockAssets.find(a => a.id === assetId);
  return asset?.code ?? assetId;
}

// Helper function to compare transaction values for sorting - extracted to reduce cognitive complexity (S3776)
function compareTransactionValues(aValue: any, bValue: any, direction: 'asc' | 'desc', key: string): number {
  // Handle null/undefined values
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  // Special handling for auditNo - extract numeric part
  if (key === 'auditNo') {
    const aNum = parseInt((aValue as string).replace(/\D/g, ''), 10);
    const bNum = parseInt((bValue as string).replace(/\D/g, ''), 10);
    return direction === 'asc' ? aNum - bNum : bNum - aNum;
  }

  // Compare string values
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    // Try to parse as dates first
    const aDate = new Date(aValue);
    const bDate = new Date(bValue);
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      return direction === 'asc'
        ? aDate.getTime() - bDate.getTime()
        : bDate.getTime() - aDate.getTime();
    }
    // Fall back to string comparison
    return direction === 'asc'
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  }

  // Compare number values
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  }

  return 0;
}

interface SortIconProps {
  readonly columnKey: keyof ExtendedTransaction;
  readonly sortConfig: { key: keyof ExtendedTransaction | null; direction: 'asc' | 'desc' | null };
}

function SortIcon({ columnKey, sortConfig }: SortIconProps) {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Mock extended transactions
const mockExtendedTransactions: ExtendedTransaction[] = mockTransactions.map((txn, index) => ({
  ...txn,
  auditNo: String(index + 1),
  assetCode: getAssetCode(txn.assetId),
  reference: `REF-${String(index + 1).padStart(4, '0')}`,
  year: new Date(txn.transactionDate).getFullYear(),
  period: new Date(txn.transactionDate).getMonth() + 1,
  startNBV: txn.amount * 1.2,
  movementValue: txn.amount,
  depreciationMovement: txn.type === 'depreciation' ? txn.amount : 0,
  endNBV: txn.amount * 0.8,
  detail: txn.description || `${txn.type} transaction`,
  targetYear: txn.type === 'transfer' ? new Date(txn.transactionDate).getFullYear() + 1 : undefined,
  targetPeriod: txn.type === 'transfer' ? new Date(txn.transactionDate).getMonth() + 2 : undefined,
  subLedger: ['FA', 'IT', 'PROP'][index % 3],
  category: getAssetCategory(txn.assetName),
  percentage: txn.type === 'depreciation' ? 20 : undefined,
  transferredTo: txn.type === 'transfer' ? 'London HQ' : undefined,
  transferredFrom: txn.type === 'transfer' ? 'Manchester Office' : undefined,
  project: ['Project Alpha', 'Project Beta', undefined][index % 3],
  costCentre: ['CC-001', 'CC-002', 'CC-003'][index % 3],
  disposalReason: txn.type === 'disposal' ? 'End of useful life' : undefined,
  user: txn.preparedBy,
}));

export function Enquiry(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useMode();
  const [transactions, setTransactions] = useState<ExtendedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null);
  const transactionRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  
  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    isPinned,
    isApplyingFilters
  } = useRightPanel();

  // Product Tour
  const { setOnStartTour } = useTour();
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useTransactionHistoryTour();

  useEffect(() => {
    setOnStartTour(() => startTour);
  }, [setOnStartTour, startTour]);

  // Column resize hook
  const initialColumnWidths = {
    select: 80,
    auditNo: 100,
    assetCode: 150,
    description: 300,
    reference: 150,
    date: 120,
    year: 80,
    period: 80,
    type: 130,
    status: 120,
    startNBV: 150,
    movementValue: 150,
    depreciationMovement: 180,
    endNBV: 150,
    detail: 250,
    targetYear: 100,
    targetPeriod: 100,
    subLedger: 120,
    category: 180,
    percentage: 100,
    transferredTo: 180,
    transferredFrom: 180,
    project: 150,
    costCentre: 120,
    disposalReason: 200,
    user: 150
  };

  const { columnWidths, handleMouseDown } = useColumnResize(initialColumnWidths, ['select']);

  // Initialize visible columns from context or defaults
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      // Initialize columns and filters for Enquiry page if not already set
      if (contextVisibleColumns.length === 0) {
        const defaultVisible = DEFAULT_ENQUIRY_COLUMNS
          .filter(col => col.visible)
          .map(col => col.id);
        setVisibleColumns(defaultVisible);
      }
      if (contextColumnOrder.length === 0) {
        setColumnOrder(DEFAULT_ENQUIRY_COLUMNS.map(col => col.id));
      }
      setIsInitialized(true);
    }
  }, [isInitialized, contextVisibleColumns.length, contextColumnOrder.length, setVisibleColumns, setColumnOrder]);

  // Use context columns or fall back to defaults
  const visibleColumns = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_ENQUIRY_COLUMNS.filter(col => col.visible).map(col => col.id);

  const columnOrder = contextColumnOrder.length > 0
    ? contextColumnOrder
    : DEFAULT_ENQUIRY_COLUMNS.map(col => col.id);

  // Build ordered visible columns for rendering
  const orderedVisibleColumns = columnOrder
    .filter(id => visibleColumns.includes(id))
    .map(id => DEFAULT_ENQUIRY_COLUMNS.find(col => col.id === id))
    .filter(Boolean) as typeof DEFAULT_ENQUIRY_COLUMNS;

  const [sortConfig, setSortConfig] = useState<{
    key: keyof ExtendedTransaction | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'auditNo',
    direction: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const mapTransactions = (data: Transaction[]): ExtendedTransaction[] => {
    return data.map((txn: Transaction, index: number) => {
      const normalizedType = txn.type.toLowerCase() as TransactionType;
      const normalizedStatus = txn.status.toLowerCase() as TransactionStatus;
      
      // For assetCode: prioritize API data, then try to extract from assetId, then use getAssetCode as last resort
      let assetCode = (txn as any).assetCode || (txn as ExtendedTransaction).assetCode;
      if (!assetCode) {
        // If assetId looks like a code (e.g., "AST-00001"), use it directly
        if (txn.assetId && /^[A-Z]+-\d+$/i.test(txn.assetId)) {
          assetCode = txn.assetId;
        } else {
          // Fall back to looking it up in mockAssets (for demo mode)
          assetCode = getAssetCode(txn.assetId);
        }
      }
      
      return {
        ...txn,
        type: normalizedType,
        status: normalizedStatus,
        auditNo: (txn as ExtendedTransaction).auditNo ?? (txn.auditNumber !== undefined ? String(txn.auditNumber) : String(index + 1)),
        assetCode,
        reference: (txn as ExtendedTransaction).reference ?? (txn as any).reference ?? `REF-${String(index + 1).padStart(4, '0')}`,
        year: (txn as ExtendedTransaction).year ?? new Date(txn.transactionDate).getFullYear(),
        period: (txn as ExtendedTransaction).period ?? new Date(txn.transactionDate).getMonth() + 1,
        startNBV: (txn as ExtendedTransaction).startNBV ?? txn.amount * 1.2,
        movementValue: (txn as ExtendedTransaction).movementValue ?? txn.amount,
        depreciationMovement: (txn as ExtendedTransaction).depreciationMovement ?? (normalizedType === 'depreciation' ? txn.amount : 0),
        endNBV: (txn as ExtendedTransaction).endNBV ?? txn.amount * 0.8,
        detail: (txn as ExtendedTransaction).detail ?? txn.description ?? `${normalizedType} transaction`,
        targetYear: (txn as ExtendedTransaction).targetYear,
        targetPeriod: (txn as ExtendedTransaction).targetPeriod,
        subLedger: (txn as ExtendedTransaction).subLedger,
        category: (txn as ExtendedTransaction).category,
        percentage: (txn as ExtendedTransaction).percentage,
        transferredTo: (txn as ExtendedTransaction).transferredTo,
        transferredFrom: (txn as ExtendedTransaction).transferredFrom,
        project: (txn as ExtendedTransaction).project,
        costCentre: (txn as ExtendedTransaction).costCentre,
        disposalReason: (txn as ExtendedTransaction).disposalReason,
        user: txn.preparedBy,
      };
    });
  };

  // Load transactions function wrapped in useCallback for refresh functionality
  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Clear transactions to prevent showing stale data while loading
      setTransactions([]);
      
      if (mode === 'demo') {
        // In demo mode, use demo data directly
        setTransactions(mockExtendedTransactions);
      } else {
        // In live mode, call API only - fall back to demo on error
        try {
          const result = await dataService.getTransactionsApiOnly({ pageSize: 1000 });
          const apiTransactions = mapTransactions(result.data);
          setTransactions(apiTransactions);
        } catch (apiError) {
          console.warn('Transactions API failed, falling back to demo:', apiError);
          setTransactions(mockExtendedTransactions);
        }
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  // Load transactions on mount and when mode changes
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadTransactions, '/enquiry');

  // Reset filters on mount (unless deep-linking with URL params)
  useEffect(() => {
    const assetIdsParam = searchParams.get('assetIds');
    const transactionIdParam = searchParams.get('transactionId');
    
    if (!assetIdsParam && !transactionIdParam) {
      // No deep-link params, reset all local filters
      setSearchQuery('');
    }
  }, []); // Only run on mount

  // Handle asset IDs from URL params
  const [urlAssetIds, setUrlAssetIds] = useState<string[]>([]);
  useEffect(() => {
    const assetIdsParam = searchParams.get('assetIds');
    if (assetIdsParam) {
      const assetIds = assetIdsParam.split(',');
      setUrlAssetIds(assetIds);
    } else {
      setUrlAssetIds([]);
    }
  }, [searchParams]);

  // Handle transaction ID from URL params - scroll to and highlight specific transaction
  useEffect(() => {
    const transactionIdParam = searchParams.get('transactionId');
    if (transactionIdParam && transactions.length > 0 && !loading) {
      setHighlightedTransactionId(transactionIdParam);
      
      // Wait for the DOM to update, then scroll to the transaction
      setTimeout(() => {
        const transactionRow = transactionRefs.current[transactionIdParam];
        if (transactionRow) {
          transactionRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedTransactionId(null);
      }, 3000);
    }
  }, [searchParams, transactions, loading]);

  // Filter transactions
  const filteredTransactions = transactions.filter((txn) => {
    // Asset ID filter (from URL params)
    if (urlAssetIds.length > 0) {
      const matchesAssetId = urlAssetIds.some(id => 
        txn.assetId.includes(id) || id.includes(txn.assetId)
      );
      if (!matchesAssetId) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        txn.assetId.toLowerCase().includes(query) ||
        txn.assetName.toLowerCase().includes(query) ||
        txn.assetCode.toLowerCase().includes(query) ||
        txn.reference.toLowerCase().includes(query) ||
        txn.auditNo.toLowerCase().includes(query) ||
        txn.detail.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Type filter (from right panel)
    if (tableFilters.type && (tableFilters.type as TransactionType[]).length > 0) {
      if (!(tableFilters.type as TransactionType[]).includes(txn.type)) return false;
    }

    // Status filter (from right panel)
    if (tableFilters.status && (tableFilters.status as TransactionStatus[]).length > 0) {
      if (!(tableFilters.status as TransactionStatus[]).includes(txn.status)) return false;
    }

    return true;
  });

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = (key: keyof ExtendedTransaction) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  };


  // Apply sorting to filtered transactions - using extracted helper function to reduce cognitive complexity (S3776)
  const sortedTransactions = [...filteredTransactions].sort((a: any, b: any) => {
    // If no sort is applied, maintain original order
    if (!sortConfig.key || !sortConfig.direction) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    return compareTransactionValues(aValue, bValue, sortConfig.direction, sortConfig.key);
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedTransactions.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransactions = sortedTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, tableFilters]);

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  // Select all handler
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedTransactions.map(txn => txn.id));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedTransactions, setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  const handleExportCSV = () => {
    try {
      // Build columns array based on visible columns
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'auditNo':
            columns.push({ key: 'auditNo', label: 'Audit No.' });
            break;
          case 'assetCode':
            columns.push({ key: 'assetCode', label: 'Asset Code' });
            break;
          case 'description':
            columns.push({ key: 'assetName', label: 'Description' });
            break;
          case 'reference':
            columns.push({ key: 'reference', label: 'Reference' });
            break;
          case 'date':
            columns.push({ key: 'transactionDate', label: 'Date', format: formatDateForExport });
            break;
          case 'year':
            columns.push({ key: 'year', label: 'Year' });
            break;
          case 'period':
            columns.push({ key: 'period', label: 'Period' });
            break;
          case 'type':
            columns.push({ key: 'type', label: 'Type' });
            break;
          case 'startNBV':
            columns.push({ key: 'startNBV', label: 'Start Net Book Value', format: formatCurrencyForExport });
            break;
          case 'movementValue':
            columns.push({ key: 'movementValue', label: 'Movement Value', format: formatCurrencyForExport });
            break;
          case 'depreciationMovement':
            columns.push({ key: 'depreciationMovement', label: 'Depreciation Movement', format: formatCurrencyForExport });
            break;
          case 'endNBV':
            columns.push({ key: 'endNBV', label: 'End Net Book Value', format: formatCurrencyForExport });
            break;
          case 'detail':
            columns.push({ key: 'detail', label: 'Detail' });
            break;
          case 'targetYear':
            columns.push({ key: 'targetYear', label: 'Target Year' });
            break;
          case 'targetPeriod':
            columns.push({ key: 'targetPeriod', label: 'Target Period' });
            break;
          case 'subLedger':
            columns.push({ key: 'subLedger', label: 'Sub Ledger' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'percentage':
            columns.push({ key: 'percentage', label: 'Percentage', format: (v) => v ? `${v}%` : '' });
            break;
          case 'transferredTo':
            columns.push({ key: 'transferredTo', label: 'Transferred To' });
            break;
          case 'transferredFrom':
            columns.push({ key: 'transferredFrom', label: 'Transferred From' });
            break;
          case 'project':
            columns.push({ key: 'project', label: 'Project' });
            break;
          case 'costCentre':
            columns.push({ key: 'costCentre', label: 'Cost Centre' });
            break;
          case 'disposalReason':
            columns.push({ key: 'disposalReason', label: 'Disposal Reason' });
            break;
          case 'user':
            columns.push({ key: 'user', label: 'User' });
            break;
        }
      });

      exportToCSV({
        filename: generateFilename('transactions-export', 'csv'),
        columns,
        data: sortedTransactions,
      });

      toast.success(`Exported ${sortedTransactions.length} transaction(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      // Build columns array based on visible columns
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'auditNo':
            columns.push({ key: 'auditNo', label: 'Audit No.' });
            break;
          case 'assetCode':
            columns.push({ key: 'assetCode', label: 'Asset Code' });
            break;
          case 'description':
            columns.push({ key: 'assetName', label: 'Description' });
            break;
          case 'reference':
            columns.push({ key: 'reference', label: 'Reference' });
            break;
          case 'date':
            columns.push({ key: 'transactionDate', label: 'Date', format: formatDateForExport });
            break;
          case 'year':
            columns.push({ key: 'year', label: 'Year' });
            break;
          case 'period':
            columns.push({ key: 'period', label: 'Period' });
            break;
          case 'type':
            columns.push({ key: 'type', label: 'Type' });
            break;
          case 'startNBV':
            columns.push({ key: 'startNBV', label: 'Start Net Book Value', format: formatCurrencyForExport });
            break;
          case 'movementValue':
            columns.push({ key: 'movementValue', label: 'Movement Value', format: formatCurrencyForExport });
            break;
          case 'depreciationMovement':
            columns.push({ key: 'depreciationMovement', label: 'Depreciation Movement', format: formatCurrencyForExport });
            break;
          case 'endNBV':
            columns.push({ key: 'endNBV', label: 'End Net Book Value', format: formatCurrencyForExport });
            break;
          case 'detail':
            columns.push({ key: 'detail', label: 'Detail' });
            break;
          case 'targetYear':
            columns.push({ key: 'targetYear', label: 'Target Year' });
            break;
          case 'targetPeriod':
            columns.push({ key: 'targetPeriod', label: 'Target Period' });
            break;
          case 'subLedger':
            columns.push({ key: 'subLedger', label: 'Sub Ledger' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'percentage':
            columns.push({ key: 'percentage', label: 'Percentage', format: (v) => v ? `${v}%` : '' });
            break;
          case 'transferredTo':
            columns.push({ key: 'transferredTo', label: 'Transferred To' });
            break;
          case 'transferredFrom':
            columns.push({ key: 'transferredFrom', label: 'Transferred From' });
            break;
          case 'project':
            columns.push({ key: 'project', label: 'Project' });
            break;
          case 'costCentre':
            columns.push({ key: 'costCentre', label: 'Cost Centre' });
            break;
          case 'disposalReason':
            columns.push({ key: 'disposalReason', label: 'Disposal Reason' });
            break;
          case 'user':
            columns.push({ key: 'user', label: 'User' });
            break;
        }
      });

      exportToExcel({
        filename: generateFilename('transactions-export', 'xls'),
        sheetName: 'Transactions',
        columns,
        data: sortedTransactions,
      });

      toast.success(`Exported ${sortedTransactions.length} transaction(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  // Helper function to get sort key for a column
  const getSortKeyForColumn = (columnId: string): keyof ExtendedTransaction | null => {
    const sortKeyMap: Record<string, keyof ExtendedTransaction> = {
      auditNo: 'auditNo',
      assetCode: 'assetCode',
      description: 'assetName',
      reference: 'reference',
      date: 'transactionDate',
      year: 'year',
      period: 'period',
      type: 'type',
      startNBV: 'startNBV',
      movementValue: 'movementValue',
      depreciationMovement: 'depreciationMovement',
      endNBV: 'endNBV',
    };
    return sortKeyMap[columnId] ?? null;
  };

  // Helper function to get status badge styling
  const getStatusBadgeStyle = (status: TransactionStatus) => {
    switch (status) {
      case 'posted':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'approved':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  // Helper function to get type badge styling
  const getTypeBadgeStyle = (type: TransactionType) => {
    switch (type) {
      case 'addition':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'depreciation':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'disposal':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'transfer':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'revaluation':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, transaction: ExtendedTransaction) => {
    switch (columnId) {
      case 'auditNo':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">{transaction.auditNo}</TableCell>;
      case 'assetCode':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${transaction.assetId}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
              data-tour="asset-link"
            >
              {transaction.assetCode}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'description':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${transaction.assetId}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {transaction.assetName}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'reference':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.reference}</TableCell>;
      case 'date':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{format(new Date(transaction.transactionDate), 'dd/MM/yyyy')}</TableCell>;
      case 'year':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{transaction.year}</TableCell>;
      case 'period':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{transaction.period}</TableCell>;
      case 'type':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${getTypeBadgeStyle(transaction.type)}`} data-tour="transaction-type">
              {transaction.type}
            </span>
          </TableCell>
        );
      case 'startNBV':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(transaction.startNBV)}</TableCell>;
      case 'movementValue':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(transaction.movementValue)}</TableCell>;
      case 'depreciationMovement':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(transaction.depreciationMovement)}</TableCell>;
      case 'endNBV':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{formatCurrency(transaction.endNBV)}</TableCell>;
      case 'detail':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.detail}</TableCell>;
      case 'targetYear':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{transaction.targetYear || '-'}</TableCell>;
      case 'targetPeriod':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{transaction.targetPeriod || '-'}</TableCell>;
      case 'subLedger':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.subLedger || '-'}</TableCell>;
      case 'category':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.category || '-'}</TableCell>;
      case 'status':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${getStatusBadgeStyle(transaction.status)}`}>
              {transaction.status}
            </span>
          </TableCell>
        );
      case 'percentage':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{transaction.percentage ? `${transaction.percentage}%` : '-'}</TableCell>;
      case 'transferredTo':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.transferredTo || '-'}</TableCell>;
      case 'transferredFrom':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.transferredFrom || '-'}</TableCell>;
      case 'project':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.project || '-'}</TableCell>;
      case 'costCentre':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.costCentre || '-'}</TableCell>;
      case 'disposalReason':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.disposalReason || '-'}</TableCell>;
      case 'user':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{transaction.user || '-'}</TableCell>;
      default:
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">-</TableCell>;
    }
  };

  // Custom Actions Tab with Export functionality
  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {/* Export All Section - Always visible */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={Download}
            title="Export to CSV"
            description="Download all transactions as CSV file"
            variant="action"
            onClick={handleExportCSV}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={handleExportExcel}
          />
        </div>

        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Select one or more transactions to see additional actions
            </p>
          </div>
        ) : (
          <>
            {/* Export Selected */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Export Selected
              </h4>
              <ActionCard
                icon={Download}
                title="Export Selected to CSV"
                description={`Download ${selectedRowIds.size} transaction${selectedRowIds.size !== 1 ? 's' : ''} as CSV`}
                variant="action"
                onClick={() => console.log('Export selected to CSV')}
              />
              <ActionCard
                icon={FileSpreadsheet}
                title="Export Selected to Excel"
                description={`Generate Excel file with ${selectedRowIds.size} transaction${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="action"
                onClick={() => console.log('Export selected to Excel')}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsForBadge = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_ENQUIRY_COLUMNS.filter(col => col.visible).map(col => col.id);

  // Define page-specific tabsfor the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <FiltersTab />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <ColumnsTab />,
      badgeCount: visibleColumnsForBadge.length,
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header - Following Team Settings Pattern */}
        <div className="border-b bg-card p-6 flex-shrink-0" data-tour="transaction-history-header">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Asset Transaction History</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  View and analyse all asset transactions and movements
                </p>
              </div>
            </div>
            
            {/* Search Bar and Export Button - Right aligned */}
                        <div className="flex items-center gap-3">
                          <EvoFeatureFeedbackCollector feature="Transaction History" />
                          <div className="relative w-80">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search transactions..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-9"
                              data-tour="search-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                {/* Table Container - Fills remaining space */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {(loading || isApplyingFilters) && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium">Loading transactions...</p>
            </div>
          </div>
        )}
        {!loading && error && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium text-destructive">Error</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && (
        <div className="flex-1 overflow-auto" data-tour="transaction-table">
          <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
            <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
              {/* Checkbox column */}
              <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                <div className="flex items-center justify-center px-3">
                  <Checkbox
                    checked={paginatedTransactions.length > 0 && paginatedTransactions.every(txn => selectedRowIds.has(txn.id))}
                    onCheckedChange={handleSelectAll}
                    data-tour="select-checkbox-input"
                  />
                </div>
                {/* Column divider - not resizable for checkbox */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                  <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                </div>
              </TableHead>
              {orderedVisibleColumns.map((column, index) => {
                const sortKey = getSortKeyForColumn(column.id);
                const isRightAligned = ['startNBV', 'movementValue', 'depreciationMovement', 'endNBV', 'percentage'].includes(column.id);

                return (
                  <TableHead 
                    key={column.id} 
                    className={`h-12 font-semibold relative ${isRightAligned ? 'text-right' : ''}`}
                    style={{ width: `${columnWidths[column.id]}px` }}
                  >
                    {sortKey ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        onClick={() => handleSort(sortKey)}
                      >
                        {column.label}
                        <SortIcon columnKey={sortKey} sortConfig={sortConfig} />
                      </Button>
                    ) : (
                      column.label
                    )}
                    {/* Resizable column divider */}
                    <div
                      className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-1 cursor-col-resize hover:bg-primary/20 group"
                      onMouseDown={(e) => handleMouseDown(column.id, e.clientX)}
                    >
                      <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-600 group-hover:bg-primary" />
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTransactions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={orderedVisibleColumns.length + 1}
                  className="h-96"
                >
                  <div className="flex flex-col items-center justify-center space-y-4 py-12">
                    <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                      <FileX className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                    </div>
                    <div className="space-y-2 text-center">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No transactions found</h3>
                      <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                        {searchQuery || (tableFilters.type && (tableFilters.type as TransactionType[]).length > 0) || (tableFilters.status && (tableFilters.status as TransactionStatus[]).length > 0)
                          ? "Try adjusting your search or filters to find what you're looking for"
                          : "No transaction history available yet"}
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTransactions.map((transaction, index) => (
                <TableRow 
                  key={transaction.id}
                  ref={(el) => {
                    transactionRefs.current[transaction.id] = el;
                  }}
                  className={`
                    ${highlightedTransactionId === transaction.id ? 'bg-primary/10' : ''}
                    ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                    hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                  `}
                >
                  {/* Checkbox cell */}
                  <TableCell style={{ width: `${columnWidths.select}px` }}>
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={selectedRowIds.has(transaction.id)}
                        onCheckedChange={(checked) => handleSelectionChange(transaction.id, checked as boolean)}
                      />
                    </div>
                  </TableCell>
                  {orderedVisibleColumns.map((column) => renderCellContent(column.id, transaction))}
                </TableRow>
              ))
            )}
            </TableBody>
          </Table>
        </div>
        )}
        </div>

        {/* Footer - Sticky at bottom, outside scrollable area */}
        {!loading && !error && sortedTransactions.length > 0 && (
          <div className="border-t bg-card px-6 py-3 flex-shrink-0">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 text-sm text-muted-foreground">
              <div className="flex-shrink-0">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedTransactions.length)} of {sortedTransactions.length} entries
                {sortedTransactions.length !== transactions.length && ` (filtered from ${transactions.length} total)`}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 flex-shrink-0" data-tour="pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                {/* Page Number Buttons */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const pageNumbers: (number | string)[] = [];
                    const maxVisiblePages = 7;
                    
                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pageNumbers.push(i);
                      }
                    } else {
                      // Always show first page
                      pageNumbers.push(1);
                      
                      if (currentPage > 3) {
                        pageNumbers.push('...');
                      }
                      
                      // Show pages around current page
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);
                      
                      for (let i = start; i <= end; i++) {
                        pageNumbers.push(i);
                      }
                      
                      if (currentPage < totalPages - 2) {
                        pageNumbers.push('...');
                      }
                      
                      // Always show last page
                      pageNumbers.push(totalPages);
                    }
                    
                    return pageNumbers.map((page, pageIndex) => {
                      if (page === '...') {
                        return (
                          <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      
                      return (
                        <Button
                          key={`page-${page}`}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(page as number)}
                          className="min-w-[36px]"
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
              )}
              
              <div className="flex-shrink-0">
                Total Movement Value: {formatCurrency(
                  sortedTransactions.reduce((sum, txn) => sum + txn.movementValue, 0)
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Page-specific Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} defaultTab="actions" />

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </PageWithRightPanel>
  );
}

export default Enquiry;
